/**
 * UseChatSession — the behavior of the PRATUS AI chat, lifted out of the legacy
 * `ChatDisasterGPT.js` render tree so the V2 drawer can be pure presentation.
 * State still lives on `dchatSlice` (poll/post/resume/cancel) and the request
 * payload is byte-for-byte what the legacy shell sends: optimistic message,
 * legend-class filters, layer prefetch + ArcGIS user tokens, and the compacted
 * history (`compactDisasterChatPayload`).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import {
  cancelRun,
  resumeDChat,
  setCurrentSitrepId,
  setSelectedDChat,
  setStatus,
} from 'slices/dchatSlice';
import { useOptionalUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/useOptionalUscgMapContext';
import { useIsCommandCenter } from 'components/MapPage/CorporateViewTabs/LayersTab/utils/isCommandCenter';
import { useChatSidebar } from 'components/GlobalChatSidebar/ChatSidebarProvider';
import { selectIncidentGuid } from 'slices/commonSelectors';
import { observability } from 'utils/observability';
import { useHighSecurityFlag } from 'utils/featureFlags';
import { defaultHubRealtimeSelections } from './dataSourceSelections';

import {
  useDeleteDisasterChat,
  usePollDChat,
  usePostDChat,
  useStopPollDChat,
} from '../hooks/useDisasterChats';
import { useAICommands, type AICommand } from '../hooks/useAICommands';
import {
  prefetchLayerFeaturesForAI,
  withLegendClassFiltersForAI,
} from '../utils/prefetchLayerFeaturesForAI';
import { compactDisasterChatPayload } from '../utils/layerSnapshotCompact';
import { withArcGisUserTokensForAI } from '../utils/arcGisUserTokenForAI';
import {
  applyDrawnAreaToLayersSelection,
  upsertSelectedLayerForAI,
} from '../utils/addSelectedLayerForAI';
import { expandSlashCommand } from '../utils/expandSlashCommand';
import type { ResumeDecision } from '../agentConfirm/types';
import {
  SERVER_IN_PROGRESS_STATUSES,
  type AgentMode,
  type ChatBlueprint,
  type ChatMessage,
  type DChat,
  type DChatClientStatus,
  type RealtimeSelection,
} from './types';
import { areSelectionsValid } from './chatHelpers';
import { useAiWorkspaceIncident } from './useAiWorkspaceIncident';

interface DchatSliceState {
  dchat: {
    dchat: DChat;
    status: DChatClientStatus;
    streamtext: string;
  };
}

/**
 * `dchatSlice` is untyped JS whose thunks resolve to values (resumeDChat →
 * boolean). Narrow `useDispatch` so those results are usable without `any`.
 */
type ThunkLike<R> = (dispatch: unknown, getState: unknown) => R;
interface AppThunkDispatch {
  <R>(thunk: ThunkLike<R>): R;
  (action: { type: string; payload?: unknown }): void;
}

export interface UseChatSessionOptions {
  /** Datetime window forwarded with each message (legacy prop passthrough). */
  selectedDatetime?: string;
  /** Called after the session is closed/deleted so the shell can hide itself. */
  onClose: () => void;
}

export interface ChatSession {
  dchat: DChat;
  messages: ChatMessage[];
  status: DChatClientStatus;
  streamtext: string;
  /** True while the agent is running (loading or ongoing). */
  isAgentRunning: boolean;
  /** Last server-side stage label shown while running. */
  priorStatus: string;
  /** Timestamp for optimistic messages of a not-yet-persisted chat. */
  sendMessageTimestamp?: Date;

  input: string;
  setInput: (value: string) => void;
  /** Bumps when a queued composer prompt is applied so the textarea can focus. */
  composerFocusNonce: number;
  submit: () => void;
  canSubmit: boolean;
  /** False when a selected source still needs configuration (e.g. SITREP id). */
  selectionsValid: boolean;

  mode: AgentMode;
  setMode: (mode: AgentMode) => void;
  commands: AICommand[];

  realtimeSelections: RealtimeSelection[];
  setRealtimeSelections: React.Dispatch<
    React.SetStateAction<RealtimeSelection[]>
  >;

  aiLayerPickMode: boolean;
  /** Undefined when the drawer is rendered outside a map page. */
  toggleAiLayerPickMode?: () => void;

  sendMessage: (
    message: string,
    newChatName?: string,
    prePromptData?: RealtimeSelection[] | null
  ) => Promise<void>;
  redoMessage: (assistantMessageIndex: number) => void;
  cancel: () => void;
  deleteSession: () => void;
  close: () => void;

  pendingConfirmations: NonNullable<DChat['pending_confirmation']>;
  confirmationAskText?: string;
  submitConfirmations: (decisions: ResumeDecision[]) => Promise<boolean>;
}

const findLayers = (
  selections: RealtimeSelection[] | null | undefined
): RealtimeSelection | undefined =>
  Array.isArray(selections)
    ? selections.find((s) => s?.source === 'Layers')
    : undefined;

export const useChatSession = ({
  selectedDatetime,
  onClose,
}: UseChatSessionOptions): ChatSession => {
  const dispatch = useDispatch() as unknown as AppThunkDispatch;

  const [input, setInput] = useState('');
  const [composerFocusNonce, setComposerFocusNonce] = useState(0);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sendMessageTimestamp, setSendMessageTimestamp] = useState<Date>();
  const [realtimeSelections, setRealtimeSelections] = useState<
    RealtimeSelection[]
  >([]);
  const [priorStatus, setPriorStatus] = useState('');
  const [mode, setMode] = useState<AgentMode>('agent_confirm');
  const hubDefaultsSeededRef = useRef(false);

  const isHub = useIsCommandCenter();
  const highSecurity = useHighSecurityFlag();
  const incident = useAiWorkspaceIncident();
  const rawDchat = useSelector((s: DchatSliceState) => s.dchat.dchat);
  const dchat = useMemo<DChat>(() => rawDchat ?? {}, [rawDchat]);
  const status = useSelector((s: DchatSliceState) => s.dchat.status);
  const streamtext = useSelector((s: DchatSliceState) => s.dchat.streamtext);

  const isAgentRunning = status === 'loading' || status === 'ongoing';

  const pollDChat = usePollDChat();
  const stopPollDChat = useStopPollDChat();
  const postDChat = usePostDChat();
  const deleteDisasterChat = useDeleteDisasterChat();
  const commands = useAICommands(mode);

  const sharepointDriveId = incident?.sharepoint_location;
  const tenantId = incident?.tenant_id;

  const mapCtx = useOptionalUscgMapContext();
  const {
    aiPickMode,
    setAiPickMode,
    queuedComposerPrompt,
    consumeQueuedComposerPrompt,
    notifyComposerSend,
  } = useChatSidebar();
  const incidentGuid = useSelector(selectIncidentGuid) as string | undefined;
  const externalLayerTimeFilterById = mapCtx?.externalLayerTimeFilterById;
  const getAddedExternalLayers = mapCtx?.getAddedExternalLayers;
  const legendClassFilterByLayerId = mapCtx?.legendClassFilterByLayerId;
  const aiLayerPickMode = (mapCtx?.aiLayerPickMode ?? false) || aiPickMode;
  const setAiLayerPickMode = mapCtx?.setAiLayerPickMode;
  const registerAiLayerPickHandler = mapCtx?.registerAiLayerPickHandler;
  const registerAiDrawnAreaHandler = mapCtx?.registerAiDrawnAreaHandler;
  const getVisibleLayersForAI = mapCtx?.getVisibleLayersForAI;

  // Map pick → Layers selection (same toasts as legacy).
  useEffect(() => {
    if (!registerAiLayerPickHandler) return undefined;
    registerAiLayerPickHandler((payload) => {
      setRealtimeSelections((prev) => {
        const { next, added } = upsertSelectedLayerForAI(
          prev as never,
          payload,
          externalLayerTimeFilterById ?? {}
        );
        if (added) {
          const filterNote = payload.spatialFilterLabel
            ? ` (${payload.spatialFilterLabel})`
            : payload.legendFilterLabel
              ? ` (${payload.legendFilterLabel})`
              : '';
          toast.success(`Added ${payload.name}${filterNote} to Layers`);
        } else {
          toast.info(`${payload.name} is already selected`);
        }
        return next as RealtimeSelection[];
      });
    });
    return () => registerAiLayerPickHandler(null);
  }, [registerAiLayerPickHandler, externalLayerTimeFilterById]);

  useEffect(() => {
    if (!registerAiDrawnAreaHandler || !getVisibleLayersForAI) return undefined;
    registerAiDrawnAreaHandler((payload) => {
      setRealtimeSelections((prev) => {
        const { next, added } = applyDrawnAreaToLayersSelection(
          prev as never,
          payload,
          getVisibleLayersForAI(),
          externalLayerTimeFilterById ?? {}
        );
        if (added) {
          toast.success('Added map area to Layers');
        } else {
          toast.info('Turn on a map layer to scope a drawn area');
        }
        return next as RealtimeSelection[];
      });
    });
    return () => registerAiDrawnAreaHandler(null);
  }, [
    registerAiDrawnAreaHandler,
    externalLayerTimeFilterById,
    getVisibleLayersForAI,
  ]);

  useEffect(() => {
    if (!aiLayerPickMode) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setAiPickMode(false);
        setAiLayerPickMode?.(false);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [aiLayerPickMode, setAiLayerPickMode, setAiPickMode]);

  useEffect(() => {
    if (!aiPickMode) return undefined;
    const onClick = (event: MouseEvent) => {
      const el = (event.target as HTMLElement | null)?.closest?.(
        '[data-ai-target]'
      );
      if (!el) return;
      event.preventDefault();
      event.stopPropagation();
      const formBaseId = el.getAttribute('data-ai-form');
      if (!formBaseId) return;
      const sectionSource = el.getAttribute('data-ai-section') || undefined;
      const label =
        el.getAttribute('data-ai-label') ||
        (sectionSource ? `${formBaseId} ${sectionSource}` : formBaseId);
      const selectionId = `${formBaseId}:${sectionSource ?? '*'}`;
      setRealtimeSelections((prev) => {
        if (
          prev.some(
            (sel) => sel.source === 'ICS Form' && sel.id === selectionId
          )
        ) {
          toast.info(`${label} is already selected`);
          return prev;
        }
        toast.success(`Added ${label} to PRATUS AI`);
        return [
          ...prev,
          {
            source: 'ICS Form',
            id: selectionId,
            description: label,
            customData: {
              formBaseId,
              sectionSource,
              label,
              incidentId: incidentGuid,
            },
          },
        ];
      });
      setAiPickMode(false);
      setAiLayerPickMode?.(false);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [aiPickMode, incidentGuid, setAiLayerPickMode, setAiPickMode]);

  const toggleAiLayerPickMode = useMemo(() => {
    return () => {
      const next = !aiLayerPickMode;
      setAiPickMode(next);
      setAiLayerPickMode?.(next);
      if (next) {
        toast.info('Click a map layer, form card, or form box — Esc to stop');
        try {
          if (!localStorage.getItem('pratus-ai-form-pick-hint')) {
            localStorage.setItem('pratus-ai-form-pick-hint', '1');
            toast.info(
              'ICS cards and boxes are selectable while this tool is on'
            );
          }
        } catch {
          // ignore private-mode storage failures
        }
      }
    };
  }, [aiLayerPickMode, setAiLayerPickMode, setAiPickMode]);

  useEffect(() => {
    if (!queuedComposerPrompt) return;
    setInput(queuedComposerPrompt);
    consumeQueuedComposerPrompt();
    setComposerFocusNonce((n) => n + 1);
  }, [queuedComposerPrompt, consumeQueuedComposerPrompt]);

  // Keep latest messages available to async sendMessage without re-creating it.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const sendMessage = useCallback(
    async (
      message: string,
      newChatName = '',
      prePromptData: RealtimeSelection[] | null = null
    ) => {
      let data: RealtimeSelection[] =
        prePromptData !== null ? prePromptData : realtimeSelections;

      const layersSelection = findLayers(data);
      if (
        layersSelection &&
        Array.isArray(layersSelection.selectedLayers) &&
        layersSelection.selectedLayers.length > 0
      ) {
        data = data.map((sel) =>
          sel.source === 'Layers'
            ? {
                ...sel,
                selectedLayers: withLegendClassFiltersForAI(
                  sel.selectedLayers ?? [],
                  legendClassFilterByLayerId ?? {}
                ),
              }
            : sel
        );
      }

      if (notifyComposerSend(message)) {
        setInput('');
        return;
      }
      const finalNewChatName = newChatName || message.slice(0, 50);

      dispatch(setStatus('loading'));
      setPriorStatus('Loading layer data…');
      const optimisticHistorySelections = compactDisasterChatPayload({
        realtimeSelections: data,
        messages: [],
      }).historyRealtimeSelections as RealtimeSelection[];
      const sendTimestamp = new Date();
      const priorMessages = messagesRef.current;
      setMessages([
        ...priorMessages,
        {
          role: 'USER',
          content: message,
          timestamp: sendTimestamp,
          realtimeSelections: optimisticHistorySelections,
          selectedDatetime,
        },
      ]);
      setInput('');
      setSendMessageTimestamp(sendTimestamp);

      try {
        const layersSel = findLayers(data);
        if (
          layersSel &&
          Array.isArray(layersSel.selectedLayers) &&
          layersSel.selectedLayers.length > 0
        ) {
          const withSnapshots = await prefetchLayerFeaturesForAI(
            layersSel.selectedLayers,
            {
              timeFiltersByLayerId: externalLayerTimeFilterById,
              getAddedExternalLayers,
            }
          );
          const withTokens = withArcGisUserTokensForAI(withSnapshots, {
            getAddedExternalLayers,
          });
          data = data.map((sel) =>
            sel.source === 'Layers'
              ? { ...sel, selectedLayers: withTokens }
              : sel
          );
        }
      } catch (error) {
        observability.captureException(error, {
          where: 'chatV2.sendMessage.prefetch',
        });
      }

      const compacted = compactDisasterChatPayload({
        realtimeSelections: data,
        messages: priorMessages,
      });

      await postDChat({
        dchatId: dchat.id,
        message,
        realtimeSelections: compacted.realtimeSelections,
        selectedDatetime,
        newChatName: finalNewChatName,
        tenantId,
        sharepointDriveId,
        messages: compacted.messages,
        mode,
      });
    },
    [
      realtimeSelections,
      dchat.id,
      selectedDatetime,
      tenantId,
      sharepointDriveId,
      postDChat,
      mode,
      externalLayerTimeFilterById,
      getAddedExternalLayers,
      legendClassFilterByLayerId,
      dispatch,
      notifyComposerSend,
    ]
  );

  const selectionsValid = useMemo(
    () => areSelectionsValid(realtimeSelections),
    [realtimeSelections]
  );
  const canSubmit =
    selectionsValid && input.trim().length > 0 && !isAgentRunning;

  const submit = useCallback(() => {
    if (!canSubmit) return;
    void sendMessage(expandSlashCommand(input, commands));
  }, [canSubmit, input, commands, sendMessage]);

  // Mode follows the persisted chat mode when (re)opening a chat.
  useEffect(() => {
    if (dchat?.id && dchat.mode) {
      setMode(dchat.mode);
    }
  }, [dchat?.id, dchat?.mode]);

  // Resume polling only for a chat already in flight on the server.
  useEffect(() => {
    if (!dchat?.id) return undefined;
    if (dchat.status && SERVER_IN_PROGRESS_STATUSES.has(dchat.status)) {
      pollDChat(dchat.id);
    }
    return () => {
      stopPollDChat();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- rebind on chat switch only
  }, [dchat?.id, pollDChat, stopPollDChat]);

  // Stage label shown while the run is in progress.
  useEffect(() => {
    if (!dchat || Object.keys(dchat).length === 0) return;
    const s = dchat.status ?? '';
    if (s !== 'Complete' && s !== 'Error') {
      setPriorStatus(s);
    } else if (
      (s === 'Complete' && priorStatus !== 'Complete') ||
      (s === 'Error' && priorStatus !== 'Error')
    ) {
      setPriorStatus('Initializing');
    } else {
      setPriorStatus(s);
    }
  }, [dchat, priorStatus]);

  // Sync server messages (+ live stream bubble) into local optimistic list.
  useEffect(() => {
    if (dchat?.messages) {
      let shown: ChatMessage[] = [...dchat.messages];
      if (
        streamtext &&
        (status === 'loading' ||
          status === 'ongoing' ||
          status === 'pending_confirmation')
      ) {
        shown = [...shown, { role: 'ASSISTANT', content: streamtext }];
      }
      setMessages(shown);
    }
    if (!dchat || Object.keys(dchat).length === 0) {
      setMessages([]);
      // New / cleared session: allow hub defaults to apply again.
      hubDefaultsSeededRef.current = false;
      if (isHub) {
        setRealtimeSelections(defaultHubRealtimeSelections(highSecurity));
        hubDefaultsSeededRef.current = true;
      }
    }
    if (dchat?.messages?.length) {
      const last = dchat.messages[dchat.messages.length - 1];
      setRealtimeSelections(last.realtimeSelections ?? []);
      hubDefaultsSeededRef.current = true;
    }
  }, [dchat, streamtext, status, isHub, highSecurity]);

  // Seed Web + weather sources once on an empty hub chat. Skip IAP and never
  // overwrite a restored chat or a user who already changed the picker.
  useEffect(() => {
    if (!isHub) return;
    const hasMessages =
      messages.length > 0 || (dchat?.messages?.length ?? 0) > 0;
    if (hasMessages) {
      hubDefaultsSeededRef.current = true;
      return;
    }
    if (hubDefaultsSeededRef.current) return;
    if (realtimeSelections.length > 0) {
      hubDefaultsSeededRef.current = true;
      return;
    }
    setRealtimeSelections(defaultHubRealtimeSelections(highSecurity));
    hubDefaultsSeededRef.current = true;
  }, [
    isHub,
    highSecurity,
    messages.length,
    dchat?.messages?.length,
    realtimeSelections.length,
  ]);

  const pendingConfirmations = useMemo(
    () =>
      dchat?.status === 'PendingConfirmation' &&
      Array.isArray(dchat?.pending_confirmation)
        ? dchat.pending_confirmation
        : [],
    [dchat?.status, dchat?.pending_confirmation]
  );
  const confirmationAskText =
    pendingConfirmations.length > 0 && streamtext ? streamtext : undefined;

  const submitConfirmations = useCallback(
    async (decisions: ResumeDecision[]): Promise<boolean> => {
      const ok = await dispatch(
        resumeDChat(dchat.id, decisions) as ThunkLike<Promise<boolean>>
      );
      return ok !== false;
    },
    [dispatch, dchat.id]
  );

  const cancel = useCallback(() => {
    void dispatch(cancelRun(dchat) as ThunkLike<Promise<void>>);
  }, [dispatch, dchat]);

  const close = useCallback(() => {
    dispatch(setSelectedDChat({}));
    dispatch(setCurrentSitrepId(undefined));
    onClose();
  }, [dispatch, onClose]);

  const deleteSession = useCallback(() => {
    if (!dchat.id) return;
    // useDeleteDisasterChat is untyped JS; its variables type infers as void.
    (deleteDisasterChat as unknown as { mutate: (id: string) => void }).mutate(
      dchat.id
    );
    close();
  }, [dchat.id, deleteDisasterChat, close]);

  const applyBlueprint = useCallback(
    (blueprint: ChatBlueprint) => {
      const structured = `# ${blueprint.title}\n\n${blueprint.description}`;
      let promptData: RealtimeSelection[] = realtimeSelections;
      if (blueprint.settings?.datasources) {
        setRealtimeSelections(blueprint.settings.datasources);
        promptData = blueprint.settings.datasources;
      }
      void sendMessage(structured, blueprint.title, promptData);
    },
    [realtimeSelections, sendMessage]
  );

  useEffect(() => {
    if (dchat && dchat.initialBlueprint && !dchat.id && !messages.length) {
      applyBlueprint(dchat.initialBlueprint);
    }
  }, [dchat, messages, applyBlueprint]);

  const redoMessage = useCallback(
    (assistantMessageIndex: number) => {
      const userMessage = messages[assistantMessageIndex - 1];
      if (!userMessage || userMessage.role !== 'USER') return;
      const trimmed = messages.slice(0, assistantMessageIndex - 1);
      setMessages(trimmed);

      void postDChat({
        dchatId: dchat.id,
        message: userMessage.content,
        realtimeSelections: userMessage.realtimeSelections,
        selectedDatetime,
        newChatName: '',
        tenantId,
        sharepointDriveId,
        messages: trimmed,
        mode,
      });

      const now = new Date();
      setMessages([
        ...trimmed,
        {
          role: 'USER',
          content: userMessage.content,
          timestamp: now,
          realtimeSelections: userMessage.realtimeSelections,
          selectedDatetime,
        },
      ]);
      setSendMessageTimestamp(now);
    },
    [
      messages,
      postDChat,
      dchat.id,
      selectedDatetime,
      tenantId,
      sharepointDriveId,
      mode,
    ]
  );

  return {
    dchat,
    messages,
    status,
    streamtext,
    isAgentRunning,
    priorStatus,
    sendMessageTimestamp,
    input,
    setInput,
    composerFocusNonce,
    submit,
    canSubmit,
    selectionsValid,
    mode,
    setMode,
    commands,
    realtimeSelections,
    setRealtimeSelections,
    aiLayerPickMode,
    toggleAiLayerPickMode,
    sendMessage,
    redoMessage,
    cancel,
    deleteSession,
    close,
    pendingConfirmations,
    confirmationAskText,
    submitConfirmations,
  };
};
